import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdvisorPlan } from '../lib/api.js';
import type { AdvisorPlan } from '@claude-code-insights/shared';

export default function AdvisorView() {
  const [goal, setGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<AdvisorPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function handlePlan() {
    const text = goal.trim();
    if (!text || loading) return;
    setLoading(true);
    setError(null);
    setPlan(null);
    try {
      const result = await getAdvisorPlan(text);
      setPlan(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-obsidian-text mb-1">Claude Code Advisor ✦</h1>
        <p className="text-sm text-obsidian-text-muted">
          Describe a goal and get a structured implementation plan drawn from your knowledge base.
        </p>
      </div>

      {/* Goal input */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handlePlan()}
          placeholder="e.g. What's the best approach to add video creation features?"
          className="flex-1 bg-obsidian-bg-secondary border border-obsidian-border rounded-lg px-4 py-2.5 text-sm text-obsidian-text placeholder:text-obsidian-text-faint focus:outline-none focus:border-obsidian-accent transition-colors"
          disabled={loading}
        />
        <button
          onClick={handlePlan}
          disabled={!goal.trim() || loading}
          className="px-4 py-2.5 bg-obsidian-accent text-white text-sm font-semibold rounded-lg hover:opacity-90 disabled:opacity-40 transition-opacity"
        >
          {loading ? '...' : 'Plan'}
        </button>
      </div>

      {/* Error */}
      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="text-2xl mb-3 animate-pulse">✦</div>
          <p className="text-sm text-obsidian-text-muted">Searching knowledge base and generating plan...</p>
        </div>
      )}

      {/* Plan output */}
      {plan && !loading && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-semibold text-obsidian-text">Implementation Plan</h2>
            <span className="text-xs text-obsidian-text-faint">
              · {plan.insightCount} insights searched · Claude Sonnet
            </span>
          </div>

          <div className="space-y-4">
            {plan.steps.map((step) => (
              <div key={step.number} className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-obsidian-accent text-white text-xs font-bold rounded-full flex items-center justify-center mt-0.5">
                  {step.number}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-obsidian-text mb-1">{step.title}</div>
                  <div className="text-sm text-obsidian-text-muted mb-2">{step.description}</div>
                  {step.insight && (
                    <div
                      className="bg-obsidian-surface border border-obsidian-border rounded-md p-3 cursor-pointer hover:border-obsidian-accent transition-colors"
                      onClick={() => navigate(`/insight/${step.insight!.insightId}`)}
                    >
                      <div className="text-xs text-obsidian-accent font-semibold uppercase tracking-wide mb-0.5">
                        Insight
                      </div>
                      <div className="text-sm font-medium text-obsidian-text">{step.insight.title}</div>
                      <div className="text-xs text-obsidian-text-muted mt-0.5">
                        {step.insight.category} · {step.insight.relevanceScore}/10
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!plan && !loading && !error && (
        <div className="text-center py-16">
          <div className="text-4xl mb-3">✦</div>
          <p className="text-sm text-obsidian-text-muted">
            Enter a goal above to generate an implementation plan
          </p>
          <p className="text-xs text-obsidian-text-faint mt-1">
            e.g. "Improve Claude Code's context awareness" or "Add Slack notifications"
          </p>
        </div>
      )}
    </div>
  );
}
